import Stack from '@mui/material/Stack';
import React, { useState } from 'react';
import { AIMedia, AIMediaStatus } from '../../../../@types';
import { BrollMediaCard } from './BrollMediaCard';
import { ConfirmDialog } from '../../../../Common/dialog';
import { useDialog } from '../../../../hooks';
import { useDeleteAiMedia } from '../../../../services';
import { Skeleton, Typography } from '@mui/material';
import { Styles } from '@t2c/shared-fe';
import InfiniteScroll from 'react-infinite-scroll-component';

interface Props {
  aiMediaData: AIMedia[];
  isImage: boolean;
  selectedIndex: number | null;
  handleOnSelectMedia: (value: number) => void;
  isLoadingAiMediaData: boolean;
  refetchAiMediaData: () => void;
  fetchNextPageAiMediaData: () => void;
  hasMoreAiMediaData: boolean;
  isTimelinePage?: boolean;
}

export const AiGeneratedMediaTab: React.FC<Props> = ({
  aiMediaData,
  isImage,
  handleOnSelectMedia,
  selectedIndex,
  isLoadingAiMediaData,
  refetchAiMediaData,
  fetchNextPageAiMediaData,
  hasMoreAiMediaData,
  isTimelinePage = false,
}) => {
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const {
    isDialogOpen: isDeleteDialogOpen,
    openDialog: openDeleteDialog,
    closeDialog: closeDeleteDialog,
  } = useDialog();

  const { mutate: mutateDeleteAiMedia, isLoading: isDeletingAiMedia } = useDeleteAiMedia();

  const handleConfirmDelete = () => {
    if (deleteIndex !== null) {
      mutateDeleteAiMedia(
        { aiMediaId: aiMediaData[deleteIndex]?.id },
        {
          onSuccess: () => {
            refetchAiMediaData();
          },
        }
      );
    }
    closeDeleteDialog();
  };

  return (
    <InfiniteScroll
      dataLength={aiMediaData?.length || 0}
      next={fetchNextPageAiMediaData}
      hasMore={hasMoreAiMediaData}
      loader={''}
      scrollableTarget="scrollableDiv"
    >
      <Stack
        id="scrollableDiv"
        direction="row"
        flexWrap="wrap"
        gap={2}
        sx={{
          ...styles.container,
          ...(isTimelinePage ? { flex: 1 } : { height: '300px' }),
        }}
      >
        {isLoadingAiMediaData ? (
          Array.from({ length: 6 }).map((_, index) => (
            <Skeleton
              variant="rectangular"
              width={200}
              height={125}
              sx={styles.skeleton}
              key={index}
            />
          ))
        ) : aiMediaData?.length === 0 ? (
          <Stack sx={styles.noDataFoundContainer}>
            <Typography sx={styles.noDataText}>No media available</Typography>
          </Stack>
        ) : (
          (aiMediaData || []).map((item, index) => (
            <Stack key={item.id}>
              <BrollMediaCard
                id={item.id}
                mediaUrl={item?.url}
                isImage={isImage}
                mediaName={item?.name}
                isSelected={selectedIndex === index}
                handleOnSelectMedia={() => handleOnSelectMedia(index)}
                handleOnDelete={() => {
                  setDeleteIndex(index);
                  openDeleteDialog();
                }}
                status={item.status}
              />
            </Stack>
          ))
        )}
        <ConfirmDialog
          shouldOpenDialog={isDeleteDialogOpen}
          text="Are you sure you want to delete this media?"
          onCancel={closeDeleteDialog}
          onConfirm={handleConfirmDelete}
          isLoading={isDeletingAiMedia}
        />
      </Stack>
    </InfiniteScroll>
  );
};

const styles: Styles = {
  container: {
    overflow: 'auto',
  },
  noDataText: {
    width: '100%',
    textAlign: 'center',
    marginTop: 2,
    color: (theme) => theme.palette.text.secondary,
  },
  skeleton: {
    borderRadius: 2,
  },
  noDataFoundContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    height: 300,
    width: '100%',
  },
};
